import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import toast from "react-hot-toast";
import { retrieveShippingInstructionByIdApi, updateShippingInstructionApi } from "../../../../components/api/ShippingInstructionApiService";
import { currencies } from "../../../../utils/menu-items";
import FilterSelect from "../../../../components/FilterSelect";
import { format, parseISO } from "date-fns";

function EditShippingInstructionCostDialog({
  open,
  onClose,
  rowData,
  onUpdated,
}) {
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [invoiceData, setInvoiceData] = useState(rowData || {});

  useEffect(() => {
     retrieveShippingInstructionByIdApi(rowData?.id)
             .then((response) => {
              setInvoiceData(response?.data);
               setLoading(false);
             })
             .catch((error) => {console.log(error.response?.data);
              setInvoiceData({});
              setLoading(false);
             });
  }, [rowData]);

  const handleTextFieldChange = (event) => {
    const { name, value } = event.target;
    setInvoiceData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCostTextFieldChange = (event) => {
    const { name, value } = event.target;
    setInvoiceData((prev) => ({
      ...prev,
      cost: {
        ...prev.cost,
        [name]: value,
      },
    }));
  };

  const renderDateField = (label, name, value, onChange) => (
    <TextField
      size="small"
      label={label}
      fullWidth
      name={name}
      margin="normal"
      value={value ? format(parseISO(value), "yyyy-MM-dd") : ""}
      type="date"
      inputProps={{ min: "1999-01-01", max: "2999-01-01" }}
      InputLabelProps={{
        shrink: true,
      }}
      onKeyDown={(e) => e.preventDefault()}
      onChange={onChange}
    />
  );

  const renderCostField = (
    label,
    totalFieldName,
    unitFieldName,
    currencyFieldName,
    dateFieldName,
    isDirect
  ) => {
    const isActualBuyingPrice = unitFieldName === "actualBuyingPrice";
    const hasActualBuyingPrice = invoiceData?.cost?.actualBuyingPrice;
    const poFinalPrice = invoiceData?.shipmentRequest?.purchaseOrder?.finalPrice;
    
    const defaultValue = isActualBuyingPrice && !hasActualBuyingPrice ? poFinalPrice : "";

    return (
    <>
      <TextField
        label={`Total ${label}`}
        name={totalFieldName}
        type="number"
        fullWidth
        margin="normal"
        helperText={isActualBuyingPrice && !hasActualBuyingPrice ? "* Using PO Final Price as default" : ""}
        value={
          isDirect
            ? invoiceData?.[totalFieldName] ||
              Math.round(
                (invoiceData?.[unitFieldName] || 0) *
                  (invoiceData?.shipmentRequest?.shipmentQuantity || 0)
              ) ||
              ""
            : invoiceData?.cost?.[totalFieldName] ||
              Math.round(
                (invoiceData?.cost?.[unitFieldName] || 0) *
                  (invoiceData?.shipmentRequest?.shipmentQuantity || 0)
              ) || 
            (isActualBuyingPrice && !hasActualBuyingPrice ? ((poFinalPrice || 0) * (invoiceData?.shipmentRequest?.shipmentQuantity || 0)): "")
        }
        onWheel={(e) => e.target.blur()}
        onChange={(event) => {
          const totalValue = parseFloat(event.target.value) || 0;
          setInvoiceData((prev) => ({
            ...prev,
            ...(isDirect
              ? {
                  [totalFieldName]: totalValue,
                  [unitFieldName]:
                    prev?.shipmentRequest?.shipmentQuantity > 0
                      ? totalValue / prev.shipmentRequest.shipmentQuantity
                      : "",
                }
              : {
                  cost: {
                    ...prev.cost,
                    [totalFieldName]: totalValue,
                    [unitFieldName]:
                      prev?.shipmentRequest?.shipmentQuantity > 0
                        ? totalValue / prev.shipmentRequest.shipmentQuantity
                        : "",
                  },
                }),
          }));
        }}
      />
      <TextField
        label={`Unit ${label}`}
        name={unitFieldName}
        type="number"
        fullWidth
        margin="normal"
        value={
        isDirect
          ? invoiceData?.[unitFieldName] || ""
          : invoiceData?.cost?.[unitFieldName] || 
            (isActualBuyingPrice && !hasActualBuyingPrice ? poFinalPrice : "")
      }
        InputProps={{
          readOnly: true,
        }}
         helperText={isActualBuyingPrice && !hasActualBuyingPrice ? "* Using PO Final Price as default" : ""}
      />
      <TextField
        label={`${label} Currency`}
        name={currencyFieldName}
        select
        fullWidth
        margin="normal"
        value={
          isDirect
            ? invoiceData?.[currencyFieldName] || ""
            : invoiceData?.cost?.[currencyFieldName] || 
            (isActualBuyingPrice && !hasActualBuyingPrice ? invoiceData?.shipmentRequest?.purchaseOrder?.finalPriceCurrency : "")
        }
        onChange={
          isDirect ? handleTextFieldChange : handleCostTextFieldChange
        }
         helperText={isActualBuyingPrice && !hasActualBuyingPrice ? "* Using PO Final Price Currency as default" : ""}
      >
        {currencies.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>
      {renderDateField(
        `${label} Date`,
        dateFieldName,
        isDirect
          ? invoiceData?.[dateFieldName]
          : invoiceData?.cost?.[dateFieldName] || 
            (isActualBuyingPrice && !hasActualBuyingPrice ? invoiceData?.shipmentRequest?.purchaseOrder?.operationDate : ""),
        isDirect ? handleTextFieldChange : handleCostTextFieldChange
      )}
    </>
  )};

  const handleSave = async () => {
    setIsSaving(true);
     try {
      // Check if actual buying price is empty and PO price exists
      const hasActualBuyingPrice = invoiceData?.cost?.actualBuyingPrice;
      const poFinalPrice = invoiceData?.shipmentRequest?.purchaseOrder?.finalPrice;
      
      let dataToSave = { ...invoiceData };

      // Only set default values if actual buying price doesn't exist
      if (!hasActualBuyingPrice && poFinalPrice) {
        dataToSave = {
          ...dataToSave,
          cost: {
            ...dataToSave.cost,
            actualBuyingPrice: poFinalPrice,
            actualBuyingPriceCurrency: invoiceData?.shipmentRequest?.purchaseOrder?.finalPriceCurrency,
            actualBuyingPriceDate: invoiceData?.shipmentRequest?.purchaseOrder?.operationDate
          }
        };
      }

      const response = await updateShippingInstructionApi(rowData.id, dataToSave);
      toast.success("Costs updated successfully.");
      onUpdated(response.data);
      onClose();

    }
     catch (error) {
      console.error(error.response?.data);
      toast.error("Failed to update costs.");
    }finally {
      setIsSaving(false);
    }
  };

  const isFieldFilled = (fieldName, isDirect) =>
    isDirect
      ? !!invoiceData?.[fieldName]
      : !!invoiceData?.cost?.[fieldName];

  return (
    <>
    {loading ? (
       <div>Loading...</div>
     ) : (
       <>
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit Shipping Costs {invoiceData?.shipmentRequest?.purchaseOrder?.incoterm ? "PO: "+invoiceData?.shipmentRequest?.purchaseOrder?.incoterm:""}{invoiceData?.shipmentRequest?.salesOrder?.incoterm ? " -SO: " + invoiceData?.shipmentRequest?.salesOrder?.incoterm:""}</DialogTitle>
      <DialogContent>
              <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>PO Buying Price</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <div>
               <TextField
               label={`Total PO Buying Price`}
        fullWidth
        margin="normal"
        value={
          (((invoiceData?.shipmentRequest?.purchaseOrder?.finalPrice ? 
            invoiceData?.shipmentRequest?.purchaseOrder?.finalPrice : 0) * 
            (invoiceData?.shipmentRequest?.shipmentQuantity ? 
              invoiceData?.shipmentRequest?.shipmentQuantity : 0)).toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })) + 
            (invoiceData?.shipmentRequest?.purchaseOrder?.finalPriceCurrency ? 
              (" " + invoiceData?.shipmentRequest?.purchaseOrder?.finalPriceCurrency) : "")
        }
        disabled
/>
      <TextField
        fullWidth
        margin="normal"
        label={`Unit PO Buying Price`}
        value={
         (invoiceData?.shipmentRequest?.purchaseOrder?.finalPrice ? `${new Intl.NumberFormat("en-US", {
                style: "decimal",
                minimumFractionDigits: 4,
              }).format(invoiceData?.shipmentRequest?.purchaseOrder?.finalPrice)}` : "") + (invoiceData?.shipmentRequest?.purchaseOrder?.finalPriceCurrency ? (" " + invoiceData?.shipmentRequest?.purchaseOrder?.finalPriceCurrency):"")
        }
        disabled
      />
       <TextField
        fullWidth
        margin="normal"
        label={`Order Issue Date`}
        value={
         invoiceData?.shipmentRequest?.purchaseOrder?.operationDate ? format(
                                  new Date(invoiceData?.shipmentRequest?.purchaseOrder?.operationDate),
                                  "dd/MMM/yyyy",
                                )
                              : ""
        }
        disabled
      />
            </div>
          </AccordionDetails>
        </Accordion>
      <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography
            sx={{
                color: isFieldFilled("actualBuyingPrice", true)
                  ? "#388e3c" 
                  : "inherit",
                fontWeight: isFieldFilled("actualBuyingPrice", true)
                  ? "bold"
                  : "normal",
              }}>Actual Buying Price</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {renderCostField(
              "Actual Buying Price",
              "actualTotalBuyingPrice",
              "actualBuyingPrice",
              "actualBuyingPriceCurrency",
              "actualBuyingPriceDate",
              false
            )}
          </AccordionDetails>
        </Accordion>
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography
            sx={{
                color: isFieldFilled("actualInsuranceCost", true)
                  ? "#388e3c" 
                  : "inherit",
                fontWeight: isFieldFilled("actualInsuranceCost", true)
                  ? "bold"
                  : "normal",
              }}>Insurance Cost</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {renderCostField(
              "Insurance Cost",
              "actualTotalInsuranceCost",
              "actualInsuranceCost",
              "actualInsuranceCurrency",
              "actualTimeOfInsurance",
              true
            )}
          </AccordionDetails>
        </Accordion>
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography   sx={{
        color: isFieldFilled("actualFreightPrice", true)
          ? "#388e3c" 
          : "inherit",
        fontWeight: isFieldFilled("actualFreightPrice", true)
          ? "bold"
          : "normal",
      }}>Freight Cost</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {renderCostField(
              "Freight Cost",
              "actualTotalFreightPrice",
              "actualFreightPrice",
              "actualFreightCurrency",
              "actualTimeOfFreight",
              true
            )}
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography
            sx={{
                color: isFieldFilled("secondFreightCost", false)
                  ? "#388e3c" 
                  : "inherit",
                fontWeight: isFieldFilled("secondFreightCost", false)
                  ? "bold"
                  : "normal",
              }}>Second Freight Cost</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {renderCostField(
              "Second Freight Cost",
              "totalSecondFreightCost",
              "secondFreightCost",
              "secondFreightCostCurrency",
              "secondFreightDate",
              false
            )}
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography
             sx={{
                color: isFieldFilled("importExportExpense", false)
                  ? "#388e3c" 
                  : "inherit",
                fontWeight: isFieldFilled("importExportExpense", false)
                  ? "bold"
                  : "normal",
              }}>Import/Export Expenses</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {renderCostField(
              "Import/Export Expenses",
              "totalImportExportExpense",
              "importExportExpense",
              "importExportExpenseCurrency",
              "importExportExpenseDate",
              false
            )}
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography
             sx={{
                color: isFieldFilled("demurrage", false)
                  ? "#388e3c" 
                  : "inherit",
                fontWeight: isFieldFilled("demurrage", false)
                  ? "bold"
                  : "normal",
              }}>Demurrage</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {renderCostField(
              "Demurrage",
              "totalDemurrage",
              "demurrage",
              "demurrageCurrency",
              "demurrageDate",
              false
            )}
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography
             sx={{
                color: isFieldFilled("fumigation", false)
                  ? "#388e3c" 
                  : "inherit",
                fontWeight: isFieldFilled("fumigation", false)
                  ? "bold"
                  : "normal",
              }}>Fumigation</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {renderCostField(
              "Fumigation",
              "totalFumigation",
              "fumigation",
              "fumigationCurrency",
              "fumigationDate",
              false
            )}
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography
             sx={{
                color: isFieldFilled("handling", false)
                  ? "#388e3c" 
                  : "inherit",
                fontWeight: isFieldFilled("handling", false)
                  ? "bold"
                  : "normal",
              }}>Handling</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {renderCostField(
              "Handling",
              "totalHandling",
              "handling",
              "handlingCurrency",
              "handlingDate",
              false
            )}
          </AccordionDetails>
        </Accordion>
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography
             sx={{
                color: isFieldFilled("debitNote", false)
                  ? "#388e3c" 
                  : "inherit",
                fontWeight: isFieldFilled("debitNote", false)
                  ? "bold"
                  : "normal",
              }}>Debit Note</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {renderCostField(
              "Debit Note",
              "totalDebitNote",
              "debitNote",
              "debitNoteCurrency",
              "debitNoteDate",
              false
            )}
            <FilterSelect
              label="Debit Note Type"
              menuType="debitNoteType"
              value={invoiceData?.cost?.debitNoteType || ""}
              onChange={(e) => handleCostTextFieldChange({ target: { name: "debitNoteType", value: e.target.value } })}
              fullWidth
            />
            <TextField
              label="Debit Note Remark"
              name="debitNoteRemark"
              fullWidth
              margin="normal"
              value={invoiceData?.cost?.debitNoteRemark || ""}
              onChange={handleCostTextFieldChange}
            />
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography
             sx={{
                color: isFieldFilled("creditNote", false)
                  ? "#388e3c" 
                  : "inherit",
                fontWeight: isFieldFilled("creditNote", false)
                  ? "bold"
                  : "normal",
              }}>Credit Note</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {renderCostField(
              "Credit Note",
              "totalCreditNote",
              "creditNote",
              "creditNoteCurrency",
              "creditNoteDate",
              false
            )}
            <FilterSelect
              label="Credit Note Type"
              menuType="creditNoteType"
              value={invoiceData?.cost?.creditNoteType || ""}
              onChange={(e) => handleCostTextFieldChange({ target: { name: "creditNoteType", value: e.target.value } })}
              fullWidth
            />
            <TextField
              label="Credit Note Remark"
              name="creditNoteRemark"
              fullWidth
              margin="normal"
              value={invoiceData?.cost?.creditNoteRemark || ""}
              onChange={handleCostTextFieldChange}
            />
          </AccordionDetails>
        </Accordion>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary"
        disabled={isSaving}>
          Cancel
        </Button>
        <Button onClick={handleSave} color="primary"
        disabled={isSaving}>
          {isSaving ? <CircularProgress size={20} /> : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
    </>
            )}
    </>
  );
}

export default EditShippingInstructionCostDialog;
